import {DateTime, Interval} from "luxon";
import {Project} from "../domain/entities/project.entity";

export class DateHelper {

    private static _dates: Map<string, DateTime> = new Map<string, DateTime>();

    private static _firstDayOfCurrentYear: Date | null = null;

    private static _localeDateFormat: string | null = null;

    public static get localeDateFormat(): string {
        if (!this._localeDateFormat) {
            const formatter = new Intl.DateTimeFormat(DateTime.now().locale!, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            }).formatToParts();
            this._localeDateFormat = formatter.map((e: any) => {
                switch (e.pressed) {
                    case 'month':
                        return 'MM';
                    case 'day':
                        return 'dd';
                    case 'year':
                        return 'yyyy';
                    default:
                        return e.value;
                }
            }).join(``);
        }

        return this._localeDateFormat;
    }

    private static _today: DateTime | null = null;

    public static get today(): DateTime {
        const current = DateTime.now().setZone(`utc`).startOf(`day`);

        // Recompute across a day boundary so a long-open session sees the new day;
        // within the same day the cached instance is reused for a stable reference.
        if (!DateHelper._today || !DateHelper._today.hasSame(current, `day`)) {
            DateHelper._today = current;
        }

        return DateHelper._today;
    };

    public static asNormalizedDateTime(date: Date): DateTime {
        return DateTime.fromJSDate(date).set({hour: 0, minute: 0, second: 0, millisecond: 0});
    }

    public static calculateDelta(project: Project, pointer: DateTime): number {

        const timeFrames = project
            .issues
            .filter(i => i.type.uid === 'f532d258-bb41-4968-9204-ec68404ab692')
            .sort((issueA, issueB) => issueA.start.valueOf() - issueB.start.valueOf());

        const nextDateInFuture = timeFrames.find(timeFrame => timeFrame.start >= pointer);
        if (nextDateInFuture) {
            return nextDateInFuture.start.diff(pointer, `days`).days;
        }

        const lastInPast = [...timeFrames].reverse().find(timeFrame => timeFrame.start < pointer);
        if (!lastInPast) {
            return -1;
        }

        return lastInPast.start.diff(pointer, `days`).days;
    }

    public static dateToString(date: Date): string {
        return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`;
    }

    public static firstDayOfCurrentYear(): Date {
        if (!this._firstDayOfCurrentYear) {
            this._firstDayOfCurrentYear = new Date();
            this._firstDayOfCurrentYear.setDate(1); // Tag des Monats
            this._firstDayOfCurrentYear.setMonth(0); // Monat des Jahres - 0 ist Januar
            this._firstDayOfCurrentYear.setHours(0, 0, 0, 0);
        }

        return this._firstDayOfCurrentYear;
    }

    public static fromDateTimeToDateString(dateTime: DateTime): string {
        return dateTime.toLocal().toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY);
    }

    public static fromDateTimeToDateTimeString(dateTime: DateTime): string {
        return dateTime.toLocal().toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS);
    }

    public static fromSQL(dateTimeString: string): DateTime {
        if (!DateHelper._dates.has(dateTimeString)) {
            DateHelper._dates.set(dateTimeString, DateHelper.normalizeTimezone(DateTime.fromSQL(dateTimeString, {zone: `utc`})));
        }

        return DateHelper._dates.get(dateTimeString)!;
    }

    public static getCssClasses(date: Date): string {
        let cssClasses = [0, 6].includes(date.getDay()) ? 'slot slot_weekend' : 'slot slot_week';
        if (date.toDateString() === (new Date).toDateString()) {
            cssClasses += ' heute';
        }

        return cssClasses;
    }

    public static latest(dateTimes: DateTime[]): DateTime | null {
        if (dateTimes.length < 1) {
            return null;
        }

        return dateTimes.reduce(
            (latestDate, currentDate) => currentDate > latestDate ? currentDate : latestDate, dateTimes[0]
        );
    }

    public static getDayOfMonth(date: Date): string {
        return String(date.getDate()).toString().padStart(2, "0")
    }

    public static getDaysBetween(start: Date, end: Date): number {
        return (Math.abs(end.getTime() - start.getTime()) / (1000 * 3600 * 24)) >> 0;
    }

    public static getDaysOfMonth(year: number, month: number): number {
        return (new Date(year, month + 1, 0)).getDate();
    }

    public static getIsoDateString(date: Date): string {
        return String(date.getFullYear()) + "-" + String(date.getMonth() + 1).toString().padStart(2, "0") + "-" + String(date.getDate()).toString().padStart(2, "0")
    }

    public static getMonthNameOfDate(date: Date) {
        return ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"][date.getMonth()];
    }

    public static getRelativeDate(date: Date, days: number): Date {
        const newDate = new Date(date);
        newDate.setHours(0, 0, 0, 0);
        newDate.setDate(newDate.getDate() + days);

        return newDate;
    }

    public static getWeekdayName(date: Date): string {
        return ["So", "Mo", "Die", "Mi", "Do", "Fr", "Sa"][date.getDay()];
    }

    public static normalizeTimezone(dateTime: DateTime): DateTime {
        if (dateTime.offset !== 0) {
            return dateTime.setZone(`utc`).startOf(`day`);
        }

        if (dateTime.hour !== 0) {
            dateTime = (dateTime.hour < 12)
                ? dateTime.startOf(`day`)
                : dateTime.startOf(`day`).plus({day: 1})
        }

        return dateTime;
    }

    public static stringToDate(dateString: string): Date {
        const date = new Date();
        date.setFullYear(parseInt(dateString.substring(0, 4)), parseInt(dateString.substring(5, 7)) - 1, parseInt(dateString.substring(8, 10)));
        date.setHours(0, 0, 0, 0);

        return date;
    }

    static intervalToLocalString(interval: Interval): string {
        const start = DateHelper.fromDateTimeToDateString(interval.start!);
        const end = DateHelper.fromDateTimeToDateString(interval.end!);

        return `${start} – ${end}`;
    }

    static parseDurationString(str: string): number {
        const matches = str.trim().match(/(\d+( )*w|\d+( )*d|\d+( )*h|\d+( )*m)|(^\d+)$/g) || [];

        let totalMinutes = 0;
        matches.forEach((match: string) => {
            switch (true) {
                case match.trim().endsWith('w'):
                    totalMinutes += parseFloat(match.replace('w', '')) * 3360;
                    break;
                case match.trim().endsWith('d'):
                    totalMinutes += parseFloat(match.replace('d', '')) * 480;
                    break;
                case match.trim().endsWith('h'):
                    totalMinutes += parseFloat(match.replace('h', '')) * 60;
                    break;
                case match.trim().endsWith('m'):
                    totalMinutes += parseFloat(match.replace('m', ''));
                    break;
                case (/^\d+$/).test(match):
                    totalMinutes += parseFloat(match) * 60;
                    break;
                default:
                // ignore else
            }
        });

        return totalMinutes;
    }

    static convertDurationToString(minutes: number): string {
        const weeks = 0; //Math.floor(minutes / 3360);
        const days = 0; // Math.floor((minutes - (weeks * 3360)) / 480);
        const hours = Math.floor(minutes / 60);
        const minutesLeft = minutes - (weeks * 3360) - (days * 480) - (hours * 60);

        let str = ``;
        if (weeks > 0) {
            str += `${weeks}w `;
        }
        if (days > 0) {
            str += `${days}d `;
        }
        if (hours > 0) {
            str += `${hours}h `;
        }

        return `${str.trim()} ${minutesLeft}m`.trim();
    }
}